import LiveFeedback from './LiveFeedback';
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import api from '../../../../Component/axios_Interceptor/api';
import { SERVER_ADDRESS } from '../../../../Component/server';
import { AdminActionButton } from '../../../../Component/UI/AdminPageHeader';
import { productRequestError } from '../productRequestError';

type Grant = {
  request_id: string; kind: 'standard' | 'linked_discount' | 'gift' | 'threshold';
  discount_type: 'amount' | 'percent'; amount: number; threshold_amount: number | null;
  source_name: string | null; target_name: string | null; granted_count: number;
  created_at: string; cancelled_at: string | null;
};
const names = { standard: '일반 할인 쿠폰', linked_discount: '연계 상품 할인', gift: '연계 상품 증정', threshold: '금액 달성 쿠폰' };
const time = (value: string) => new Date(value).toLocaleString('ko-KR', { timeZone: 'Asia/Seoul', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });

export default function LiveCouponHistory({ liveSeq, disabled, onBusyChange, refreshKey = 0 }: {
  liveSeq: number; disabled: boolean; onBusyChange: (busy: boolean) => void; refreshKey?: number;
}) {
  const [grants, setGrants] = useState<Grant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [revision, setRevision] = useState(0);
  const [confirmId, setConfirmId] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [cancellationReady, setCancellationReady] = useState(false);
  const [message, setMessage] = useState('');
  const busy = useRef(false);
  useEffect(() => {
    let active = true;
    setLoading(true); setError('');
    api.get(`${SERVER_ADDRESS}orders/discount-coupon-history`, { params: { live_seq: liveSeq }, timeout: 10000 })
      .then(({ data }) => {
        if (!Array.isArray(data?.grants)) throw new Error('지급 목록 응답을 확인하지 못했습니다. 다시 시도해주세요.');
        if (active) { setGrants(data.grants); setCancellationReady(data.cancellation_ready === true); }
      }).catch(failure => { if (active) setError(productRequestError(failure, '지급 목록을 불러오지 못했습니다. 다시 시도해주세요.')); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [liveSeq, revision, refreshKey]);
  const cancel = async (id: string) => {
    if (busy.current || disabled || !cancellationReady) return;
    busy.current = true; setCancelling(true); onBusyChange(true); setError(''); setMessage('');
    try {
      const { data } = await api.post(`${SERVER_ADDRESS}orders/discount-coupon-cancel`, { live_seq: liveSeq, request_id: id });
      if (data?.request_id !== id || !data?.cancelled_at) throw new Error('쿠폰 취소 결과를 확인하지 못했습니다. 목록을 새로고침해주세요.');
      setGrants(rows => rows.map(row => row.request_id === id ? { ...row, cancelled_at: data.cancelled_at } : row));
      setConfirmId(''); setMessage('쿠폰을 취소했습니다. 기존 주문과 할인은 유지됩니다.');
    } catch (failure) { setError(productRequestError(failure, '쿠폰을 취소하지 못했습니다. 다시 시도해주세요.')); }
    finally { busy.current = false; setCancelling(false); onBusyChange(false); }
  };
  return <History aria-label="할인 쿠폰 지급 목록">
    <header><h3>할인 쿠폰 지급 목록</h3><AdminActionButton disabled={loading || cancelling} onClick={() => setRevision(value => value + 1)}>새로고침</AdminActionButton></header>
    {loading ? <p role="status">지급 목록을 불러오는 중…</p> : error ? <LiveFeedback role="alert">{error}</LiveFeedback> : grants.length === 0 ? <Empty>아직 지급한 쿠폰이 없습니다.</Empty> : <ol>
      {grants.map(grant => <li key={grant.request_id}>
        <div className="row"><strong>{names[grant.kind]}</strong><span className={grant.cancelled_at ? 'cancelled' : 'active'}>{grant.cancelled_at ? '취소됨' : '적용 중'}</span></div>
        {grant.kind === 'threshold' ? <p>누적 {Number(grant.threshold_amount).toLocaleString()}원 달성 시</p>
          : grant.kind !== 'standard' && <p className="products">{grant.source_name || '삭제된 A 상품'} → {grant.target_name || '삭제된 B 상품'}</p>}
        <div className="row"><b>{grant.kind === 'gift' ? 'B 상품 1개 증정' : `${Number(grant.amount).toLocaleString()}${grant.discount_type === 'percent' ? '%' : '원'} 할인`}</b><span>{grant.granted_count.toLocaleString()}명 지급</span></div>
        <p className="time">{time(grant.created_at)} 지급{grant.cancelled_at && ` · ${time(grant.cancelled_at)} 취소`}</p>
        {!grant.cancelled_at && (confirmId === grant.request_id ? <div className="confirm">
          <p>기존 주문·할인은 유지하고 이후 구매부터 적용을 중지합니다.</p>
          <div className="row"><AdminActionButton disabled={cancelling} onClick={() => setConfirmId('')}>돌아가기</AdminActionButton>
            <AdminActionButton $accented disabled={disabled || cancelling} onClick={() => void cancel(grant.request_id)}>{cancelling ? '취소 중…' : '지급 취소 확인'}</AdminActionButton></div>
        </div> : <AdminActionButton disabled={disabled || !cancellationReady} onClick={() => { setConfirmId(grant.request_id); setMessage(''); }}>지급 취소</AdminActionButton>)}
      </li>)}
    </ol>}
    {!loading && grants.some(grant => !grant.cancelled_at) && !cancellationReady && <p>현재 환경에서는 지급 목록만 확인할 수 있습니다. 쿠폰 취소는 아직 사용할 수 없습니다.</p>}
    {message && <LiveFeedback role="status">{message}</LiveFeedback>}
  </History>;
}

const History = styled.section`
  min-width: 0; border-left: 1px solid var(--color-border-default); padding-left: var(--space-6);
  font-size: var(--text-body); line-height: var(--leading-body);
  header, .row { display: flex; justify-content: space-between; align-items: center; gap: var(--space-3); }
  header { margin-bottom: var(--space-4); }
  h3 { margin: 0; font-size: var(--text-h3); font-weight: var(--font-weight-semibold); }
  p { margin: var(--space-2) 0; color: var(--color-text-secondary); }
  ol { list-style: none; padding: 0; margin: 0; max-height: 42dvh; overflow-y: auto; scrollbar-color: var(--color-border-default) transparent; }
  li { padding: var(--space-4) 0; border-bottom: 1px solid var(--color-border-default); }
  li:first-child { padding-top: 0; }
  li > button { margin-top: var(--space-2); }
  .products { overflow-wrap: anywhere; }
  .active { color: var(--color-text-link); }
  .cancelled, .time { color: var(--color-text-secondary); font-size: var(--text-caption); }
  .row > span { flex-shrink: 0; font-variant-numeric: tabular-nums; }
  .confirm { margin-top: var(--space-3); padding: var(--space-3); background: var(--color-bg-subtle); border-radius: var(--radius-md); }
  .confirm .row { justify-content: flex-end; flex-wrap: wrap; }
  [role='alert'] { color: var(--color-text-danger); }
  [role='status'] { color: var(--color-success); }
  @media (max-width: 860px) { border-left: 0; padding-left: 0; border-top: 1px solid var(--color-border-default); padding-top: var(--space-6); ol { max-height: none; } }
`;
const Empty = styled.p`padding: var(--space-6) 0; text-align: center;`;
